import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Search,
  MoreVertical,
  Eye,
  Check,
  X,
  Ban,
  RefreshCw,
  DollarSign,
  Users,
  TrendingUp,
  Clock,
  CreditCard,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Filter
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { affiliateService, Affiliate, AffiliateStats, Commission } from '../../services/affiliateService';

const AdminAffiliatesPage: React.FC = () => {
  const { t } = useTranslation();
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState<'affiliates' | 'commissions'>('affiliates');
  const [selectedAffiliate, setSelectedAffiliate] = useState<(Affiliate & { stats?: AffiliateStats }) | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [selectedCommissions, setSelectedCommissions] = useState<string[]>([]);
  const [commissionStatusFilter, setCommissionStatusFilter] = useState('');

  // Summary stats
  const [stats, setStats] = useState({
    totalAffiliates: 0,
    pendingApproval: 0,
    totalCommissions: 0,
    pendingPayouts: 0
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'affiliates') {
        loadAffiliates();
      } else {
        loadCommissions();
      }
    }, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [page, statusFilter, activeTab, commissionStatusFilter, search]);

  const loadAffiliates = async () => {
    setIsLoading(true);
    try {
      const response = await affiliateService.adminGetAffiliates({
        page,
        limit: 15,
        search: search || undefined,
        status: statusFilter || undefined
      });
      if (response.success && response.data) {
        setAffiliates(response.data);
        setTotalPages(response.pagination?.totalPages || 1);

        // Calculate stats
        const pending = response.data.filter(a => a.status === 'pending').length;
        setStats(prev => ({
          ...prev,
          totalAffiliates: response.pagination?.total || response.data.length,
          pendingApproval: pending
        }));
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadCommissions = async () => {
    setIsLoading(true);
    try {
      const response = await affiliateService.adminGetCommissions({
        page,
        limit: 15,
        status: commissionStatusFilter || undefined
      });
      if (response.success && response.data) {
        setCommissions(response.data);
        setTotalPages(response.pagination?.totalPages || 1);

        const pending = response.data.filter(c => c.status === 'approved').length;
        setStats(prev => ({
          ...prev,
          totalCommissions: response.pagination?.total || response.data.length,
          pendingPayouts: pending
        }));
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadAffiliates();
  };

  const handleViewAffiliate = async (affiliate: Affiliate) => {
    try {
      const response = await affiliateService.adminGetAffiliate(affiliate.id);
      if (response.success && response.data) {
        setSelectedAffiliate(response.data);
        setIsDetailModalOpen(true);
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    }
    setIsActionMenuOpen(null);
  };

  const handleApprove = async (affiliateId: string) => {
    setIsUpdating(affiliateId);
    try {
      await affiliateService.adminApproveAffiliate(affiliateId);
      toast.success(t('admin.affiliates.approved'));
      loadAffiliates();
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsUpdating(null);
      setIsActionMenuOpen(null);
    }
  };

  const handleReject = async (affiliateId: string) => {
    if (!confirm(t('admin.affiliates.confirm_reject'))) return;
    setIsUpdating(affiliateId);
    try {
      await affiliateService.adminRejectAffiliate(affiliateId);
      toast.success(t('admin.affiliates.rejected'));
      loadAffiliates();
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsUpdating(null);
      setIsActionMenuOpen(null);
    }
  };

  const handleSuspend = async (affiliateId: string) => {
    if (!confirm(t('admin.affiliates.confirm_suspend'))) return;
    setIsUpdating(affiliateId);
    try {
      await affiliateService.adminSuspendAffiliate(affiliateId);
      toast.success(t('admin.affiliates.suspended'));
      loadAffiliates();
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsUpdating(null);
      setIsActionMenuOpen(null);
    }
  };

  const handleReactivate = async (affiliateId: string) => {
    setIsUpdating(affiliateId);
    try {
      await affiliateService.adminReactivateAffiliate(affiliateId);
      toast.success(t('admin.affiliates.reactivated'));
      loadAffiliates();
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsUpdating(null);
      setIsActionMenuOpen(null);
    }
  };

  const handleApproveCommission = async (commissionId: string) => {
    setIsUpdating(commissionId);
    try {
      await affiliateService.adminApproveCommission(commissionId);
      toast.success(t('admin.affiliates.commission_approved'));
      loadCommissions();
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setIsUpdating(null);
    }
  };

  const handleMarkAsPaid = async () => {
    if (selectedCommissions.length === 0) {
      toast.error(t('admin.affiliates.no_commissions_selected'));
      return;
    }
    if (!confirm(t('admin.affiliates.confirm_pay'))) return;

    try {
      const response = await affiliateService.adminMarkCommissionsAsPaid(selectedCommissions);
      if (response.success) {
        toast.success(t('admin.affiliates.commissions_paid', { count: response.data?.success.length || 0 }));
        setSelectedCommissions([]);
        loadCommissions();
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const toggleCommissionSelection = (commissionId: string) => {
    setSelectedCommissions(prev =>
      prev.includes(commissionId)
        ? prev.filter(id => id !== commissionId)
        : [...prev, commissionId]
    );
  };

  const selectAllApproved = () => {
    const approvedIds = commissions.filter(c => c.status === 'approved').map(c => c.id);
    setSelectedCommissions(approvedIds);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'rejected':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'suspended':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
      case 'paid':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('admin.affiliates.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('admin.affiliates.description')}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.affiliates.total_affiliates')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalAffiliates}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.affiliates.pending_approval')}</p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.pendingApproval}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.affiliates.total_commissions')}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalCommissions}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('admin.affiliates.pending_payouts')}</p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats.pendingPayouts}</p>
              </div>
              <CreditCard className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => { setActiveTab('affiliates'); setPage(1); }}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'affiliates'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          {t('admin.affiliates.tab_affiliates')}
        </button>
        <button
          onClick={() => { setActiveTab('commissions'); setPage(1); }}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'commissions'
              ? 'border-primary-500 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          {t('admin.affiliates.tab_commissions')}
        </button>
      </div>

      {/* Affiliates Tab */}
      {activeTab === 'affiliates' && (
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <Input
                wrapperClassName="w-full sm:w-64"
                placeholder={t('common.search')}
                value={search}
                onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                leftIcon={<Search className="w-4 h-4" />}
              />
              <Select
                wrapperClassName="w-full sm:w-48"
                options={[
                  { value: '', label: t('admin.affiliates.all_statuses') },
                  { value: 'pending', label: t('admin.affiliates.status_pending') },
                  { value: 'approved', label: t('admin.affiliates.status_approved') },
                  { value: 'rejected', label: t('admin.affiliates.status_rejected') },
                  { value: 'suspended', label: t('admin.affiliates.status_suspended') }
                ]}
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              </div>
            ) : affiliates.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{t('admin.affiliates.no_affiliates')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto scrollbar-hide">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-2 sm:px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('admin.affiliates.user')}</th>
                      <th className="text-left py-3 px-2 sm:px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('admin.affiliates.referral_code')}</th>
                      <th className="text-left py-3 px-2 sm:px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('admin.affiliates.commission')}</th>
                      <th className="text-left py-3 px-2 sm:px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('admin.affiliates.earnings')}</th>
                      <th className="text-left py-3 px-2 sm:px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('admin.affiliates.status')}</th>
                      <th className="text-right py-3 px-2 sm:px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affiliates.map((affiliate) => (
                      <tr key={affiliate.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="py-3 px-2 sm:px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                              <span className="text-sm font-semibold text-primary-600">
                                {affiliate.user?.first_name?.charAt(0) || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {affiliate.user?.first_name} {affiliate.user?.last_name}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{affiliate.user?.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2 sm:px-4">
                          <code className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm">
                            {affiliate.referral_code}
                          </code>
                        </td>
                        <td className="py-3 px-2 sm:px-4">
                          <span className="font-medium text-gray-900 dark:text-white">{affiliate.commission_percentage}%</span>
                        </td>
                        <td className="py-3 px-2 sm:px-4">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {formatCurrency(Number(affiliate.total_earnings))}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatCurrency(Number(affiliate.pending_earnings))} {t('admin.affiliates.pending')}
                          </p>
                        </td>
                        <td className="py-3 px-2 sm:px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(affiliate.status)}`}>
                            {t(`admin.affiliates.status_${affiliate.status}`)}
                          </span>
                        </td>
                        <td className="py-3 px-2 sm:px-4">
                          <div className="flex items-center justify-end gap-2">
                            {affiliate.status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleApprove(affiliate.id)}
                                  disabled={isUpdating === affiliate.id}
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
                                  title={t('admin.affiliates.approve')}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleReject(affiliate.id)}
                                  disabled={isUpdating === affiliate.id}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                                  title={t('admin.affiliates.reject')}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {affiliate.status === 'approved' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSuspend(affiliate.id)}
                                disabled={isUpdating === affiliate.id}
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/20"
                                title={t('admin.affiliates.suspend')}
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                            )}
                            {affiliate.status === 'suspended' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReactivate(affiliate.id)}
                                disabled={isUpdating === affiliate.id}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
                                title={t('admin.affiliates.reactivate')}
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewAffiliate(affiliate)}
                              title={t('common.view')}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('common.page')} {page} {t('common.of')} {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Commissions Tab */}
      {activeTab === 'commissions' && (
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Select
                  wrapperClassName="w-full sm:w-48"
                  options={[
                    { value: '', label: t('admin.affiliates.all_statuses') },
                    { value: 'pending', label: t('admin.affiliates.commission_pending') },
                    { value: 'approved', label: t('admin.affiliates.commission_approved') },
                    { value: 'paid', label: t('admin.affiliates.commission_paid') }
                  ]}
                  value={commissionStatusFilter}
                  onChange={(e) => { setCommissionStatusFilter(e.target.value); setPage(1); }}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm" onClick={selectAllApproved}>
                  {t('admin.affiliates.select_all_approved')}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleMarkAsPaid}
                  disabled={selectedCommissions.length === 0}
                  leftIcon={<CreditCard className="w-4 h-4" />}
                >
                  {t('admin.affiliates.mark_as_paid')} ({selectedCommissions.length})
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              </div>
            ) : commissions.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{t('admin.affiliates.no_commissions')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto scrollbar-hide">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-2 sm:px-4 w-8">
                        <input
                          type="checkbox"
                          checked={selectedCommissions.length === commissions.filter(c => c.status === 'approved').length && selectedCommissions.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              selectAllApproved();
                            } else {
                              setSelectedCommissions([]);
                            }
                          }}
                          className="rounded border-gray-300 dark:border-gray-600"
                        />
                      </th>
                      <th className="text-left py-3 px-2 sm:px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('admin.affiliates.affiliate')}</th>
                      <th className="text-left py-3 px-2 sm:px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('admin.affiliates.referred_user')}</th>
                      <th className="text-left py-3 px-2 sm:px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('admin.affiliates.plan')}</th>
                      <th className="text-left py-3 px-2 sm:px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('admin.affiliates.amount')}</th>
                      <th className="text-left py-3 px-2 sm:px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('admin.affiliates.status')}</th>
                      <th className="text-right py-3 px-2 sm:px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map((commission) => (
                      <tr key={commission.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="py-3 px-2 sm:px-4">
                          <input
                            type="checkbox"
                            checked={selectedCommissions.includes(commission.id)}
                            onChange={() => toggleCommissionSelection(commission.id)}
                            disabled={commission.status !== 'approved'}
                            className="rounded border-gray-300 dark:border-gray-600"
                          />
                        </td>
                        <td className="py-3 px-2 sm:px-4">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {commission.affiliate?.user?.first_name} {commission.affiliate?.user?.last_name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{commission.affiliate?.user?.email}</p>
                        </td>
                        <td className="py-3 px-2 sm:px-4">
                          <p className="text-gray-900 dark:text-white">
                            {commission.referral?.referredUser?.first_name} {commission.referral?.referredUser?.last_name}
                          </p>
                        </td>
                        <td className="py-3 px-2 sm:px-4">
                          <p className="text-gray-900 dark:text-white">{commission.subscription?.plan?.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{commission.percentage}% commission</p>
                        </td>
                        <td className="py-3 px-2 sm:px-4">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {formatCurrency(Number(commission.amount))}
                          </p>
                        </td>
                        <td className="py-3 px-2 sm:px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(commission.status)}`}>
                            {t(`admin.affiliates.commission_${commission.status}`)}
                          </span>
                        </td>
                        <td className="py-3 px-2 sm:px-4">
                          <div className="flex items-center justify-end gap-2">
                            {commission.status === 'pending' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApproveCommission(commission.id)}
                                disabled={isUpdating === commission.id}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
                                title={t('admin.affiliates.approve')}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('common.page')} {page} {t('common.of')} {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Affiliate Detail Modal */}
      {isDetailModalOpen && selectedAffiliate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('admin.affiliates.affiliate_details')}
              </h3>
              <button
                onClick={() => setIsDetailModalOpen(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <span className="text-2xl font-semibold text-primary-600">
                    {selectedAffiliate.user?.first_name?.charAt(0) || '?'}
                  </span>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 dark:text-white">
                    {selectedAffiliate.user?.first_name} {selectedAffiliate.user?.last_name}
                  </h4>
                  <p className="text-gray-500 dark:text-gray-400">{selectedAffiliate.user?.email}</p>
                  <span className={`mt-1 inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(selectedAffiliate.status)}`}>
                    {t(`admin.affiliates.status_${selectedAffiliate.status}`)}
                  </span>
                </div>
              </div>

              {selectedAffiliate.stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-center">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedAffiliate.stats.total_referrals}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.affiliates.referrals')}</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-center">
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {selectedAffiliate.stats.conversion_rate}%
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.affiliates.conversion')}</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-center">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {formatCurrency(Number(selectedAffiliate.stats.total_earnings))}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.affiliates.total_earnings')}</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-center">
                    <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                      {formatCurrency(Number(selectedAffiliate.stats.pending_earnings))}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.affiliates.pending')}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">{t('admin.affiliates.referral_code')}</label>
                  <p className="font-mono font-bold text-lg text-gray-900 dark:text-white">{selectedAffiliate.referral_code}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500 dark:text-gray-400">{t('admin.affiliates.commission_rate')}</label>
                  <p className="font-bold text-lg text-gray-900 dark:text-white">{selectedAffiliate.commission_percentage}%</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="ghost" onClick={() => setIsDetailModalOpen(false)}>
                {t('common.close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAffiliatesPage;
